/* eslint-disable camelcase */
import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';
import {
  IAcumulador,
  IAcumuladorBulkPayload,
  IAcumuladorPayload,
  IAcumuladorSugerido,
  IClassificacaoOption,
  ILivroFiscalNota,
  ILivroFiscalResumo,
  ILivrosResumoConsolidado,
  ILivroTotalPorAcumulador,
  ILivroTotalPorCfop,
  IDifalCarteiraEmpresa,
  IDifalCarteiraKpis,
  IDifalNota,
  IDifalPorUf,
  IDifalResumo,
  LivroTipoSingular,
} from '../types/icms';

const ICMS_BASE = '/api/v1/icms';

export const useIcms = () => {
  // ===== ACUMULADORES =====

  const getClassificacoesAcumulador = async (): Promise<IClassificacaoOption[]> => {
    const res = await api.get<IDataNoPagination<IClassificacaoOption[]>>(
      `${ICMS_BASE}/acumuladores/classificacoes/`,
    );
    return res.data.data;
  };

  const getAcumuladores = async (key: QueryFunctionContext): Promise<IData<IAcumulador[]>> => {
    const [, params] = key.queryKey as [
      string,
      {
        codi_emp: number;
        tipo_operacao?: string;
        classificacao?: string;
        search?: string;
        page?: number;
        size?: number;
      },
    ];
    const { codi_emp, ...filters } = params;
    const res = await api.get<IData<IAcumulador[]>>(`${ICMS_BASE}/acumuladores/${codi_emp}/`, {
      params: filters,
    });
    return res.data;
  };

  const getAcumuladoresSugeridos = async (
    key: QueryFunctionContext,
  ): Promise<IAcumuladorSugerido[]> => {
    const [, params] = key.queryKey as [
      string,
      { codi_emp: number; dt_inicio?: string; dt_fim?: string },
    ];
    const { codi_emp, ...filters } = params;
    const res = await api.get<IDataNoPagination<IAcumuladorSugerido[]>>(
      `${ICMS_BASE}/acumuladores/${codi_emp}/sugeridos/`,
      { params: filters },
    );
    return res.data.data;
  };

  const upsertAcumulador = async (
    codi_emp: number,
    payload: IAcumuladorPayload,
  ): Promise<IAcumulador> => {
    const res = await api.post<IDataNoPagination<IAcumulador>>(
      `${ICMS_BASE}/acumuladores/${codi_emp}/`,
      payload,
    );
    return res.data.data;
  };

  const upsertAcumuladoresBulk = async (
    codi_emp: number,
    payload: IAcumuladorBulkPayload,
  ): Promise<IAcumulador[]> => {
    const res = await api.post<IDataNoPagination<IAcumulador[]>>(
      `${ICMS_BASE}/acumuladores/${codi_emp}/bulk/`,
      payload,
    );
    return res.data.data;
  };

  const updateAcumulador = async (
    codi_emp: number,
    codi_acu: string,
    payload: Partial<IAcumuladorPayload>,
  ): Promise<IAcumulador> => {
    const res = await api.put<IDataNoPagination<IAcumulador>>(
      `${ICMS_BASE}/acumuladores/${codi_emp}/${codi_acu}/`,
      payload,
    );
    return res.data.data;
  };

  const deleteAcumulador = async (codi_emp: number, codi_acu: string): Promise<void> => {
    await api.delete(`${ICMS_BASE}/acumuladores/${codi_emp}/${codi_acu}/`);
  };

  // ===== LIVROS FISCAIS =====

  const getLivroFiscal = async (key: QueryFunctionContext): Promise<IData<ILivroFiscalNota[]>> => {
    const [, params] = key.queryKey as [
      string,
      {
        codi_emp: number;
        tipo: 'entradas' | 'saidas' | 'servicos';
        dt_inicio: string;
        dt_fim: string;
        cfop?: string;
        codi_acu?: string;
        uf_clifor?: string;
        cgce_clifor?: string;
        serie?: string;
        modelo?: string;
        situacao?: string;
        search?: string;
        page?: number;
        size?: number;
      },
    ];
    const { codi_emp, tipo, ...filters } = params;
    const res = await api.get<IData<ILivroFiscalNota[]>>(
      `${ICMS_BASE}/livros-fiscais/${codi_emp}/${tipo}/`,
      { params: filters },
    );
    return res.data;
  };

  const getLivrosFiscaisResumo = async (
    key: QueryFunctionContext,
  ): Promise<ILivrosResumoConsolidado> => {
    const [, params] = key.queryKey as [
      string,
      { codi_emp: number; dt_inicio: string; dt_fim: string },
    ];
    const { codi_emp, ...filters } = params;
    const res = await api.get<IDataNoPagination<ILivrosResumoConsolidado>>(
      `${ICMS_BASE}/livros-fiscais/${codi_emp}/resumo/`,
      { params: filters },
    );
    return res.data.data;
  };

  const getLivroFiscalResumoTipo = async (
    key: QueryFunctionContext,
  ): Promise<ILivroFiscalResumo> => {
    const [, params] = key.queryKey as [
      string,
      { codi_emp: number; tipo: LivroTipoSingular; dt_inicio: string; dt_fim: string },
    ];
    const { codi_emp, tipo, ...filters } = params;
    const res = await api.get<IDataNoPagination<ILivroFiscalResumo>>(
      `${ICMS_BASE}/livros-fiscais/${codi_emp}/${tipo}/resumo/`,
      { params: filters },
    );
    return res.data.data;
  };

  const getLivroTotaisPorCfop = async (
    key: QueryFunctionContext,
  ): Promise<ILivroTotalPorCfop[]> => {
    const [, params] = key.queryKey as [
      string,
      { codi_emp: number; tipo: LivroTipoSingular; dt_inicio: string; dt_fim: string },
    ];
    const { codi_emp, tipo, ...filters } = params;
    const res = await api.get<IDataNoPagination<ILivroTotalPorCfop[]>>(
      `${ICMS_BASE}/livros-fiscais/${codi_emp}/${tipo}/totais-por-cfop/`,
      { params: filters },
    );
    return res.data.data;
  };

  const getLivroTotaisPorAcumulador = async (
    key: QueryFunctionContext,
  ): Promise<ILivroTotalPorAcumulador[]> => {
    const [, params] = key.queryKey as [
      string,
      { codi_emp: number; tipo: LivroTipoSingular; dt_inicio: string; dt_fim: string },
    ];
    const { codi_emp, tipo, ...filters } = params;
    const res = await api.get<IDataNoPagination<ILivroTotalPorAcumulador[]>>(
      `${ICMS_BASE}/livros-fiscais/${codi_emp}/${tipo}/totais-por-acumulador/`,
      { params: filters },
    );
    return res.data.data;
  };

  // ===== DIFAL =====

  const getDifalNotas = async (key: QueryFunctionContext): Promise<IData<IDifalNota[]>> => {
    const [, params] = key.queryKey as [
      string,
      {
        codi_emp: number;
        dt_inicio: string;
        dt_fim: string;
        uf_destino?: string;
        codi_acu?: string;
        tipo?: 'entrada' | 'saida';
        page?: number;
        size?: number;
      },
    ];
    const { codi_emp, ...filters } = params;
    const res = await api.get<IData<IDifalNota[]>>(`${ICMS_BASE}/difal/${codi_emp}/notas/`, {
      params: filters,
    });
    return res.data;
  };

  const getDifalResumo = async (key: QueryFunctionContext): Promise<IDifalResumo> => {
    const [, params] = key.queryKey as [
      string,
      { codi_emp: number; dt_inicio: string; dt_fim: string },
    ];
    const { codi_emp, ...filters } = params;
    const res = await api.get<IDataNoPagination<IDifalResumo>>(
      `${ICMS_BASE}/difal/${codi_emp}/resumo/`,
      { params: filters },
    );
    return res.data.data;
  };

  const getDifalPorUf = async (key: QueryFunctionContext): Promise<IDifalPorUf[]> => {
    const [, params] = key.queryKey as [
      string,
      { codi_emp: number; dt_inicio: string; dt_fim: string },
    ];
    const { codi_emp, ...filters } = params;
    const res = await api.get<
      IDataNoPagination<{ itens: IDifalPorUf[]; total_difal: number | string }>
    >(`${ICMS_BASE}/difal/${codi_emp}/por-uf/`, { params: filters });
    return res.data.data?.itens ?? [];
  };

  const getDifalCarteiraKpis = async (key: QueryFunctionContext): Promise<IDifalCarteiraKpis> => {
    const [, params] = key.queryKey as [string, Record<string, string | number | undefined>];
    const res = await api.get<IDataNoPagination<IDifalCarteiraKpis>>(
      `${ICMS_BASE}/difal/carteira/kpis/`,
      { params },
    );
    return res.data.data;
  };

  const getDifalCarteiraEmpresas = async (
    key: QueryFunctionContext,
  ): Promise<IData<IDifalCarteiraEmpresa[]>> => {
    const [, params] = key.queryKey as [string, Record<string, string | number | undefined>];
    const res = await api.get<IData<IDifalCarteiraEmpresa[]>>(
      `${ICMS_BASE}/difal/carteira/empresas/`,
      { params },
    );
    return res.data;
  };

  return {
    // Acumuladores
    getClassificacoesAcumulador,
    getAcumuladores,
    getAcumuladoresSugeridos,
    upsertAcumulador,
    upsertAcumuladoresBulk,
    updateAcumulador,
    deleteAcumulador,
    // Livros fiscais
    getLivroFiscal,
    getLivrosFiscaisResumo,
    getLivroFiscalResumoTipo,
    getLivroTotaisPorCfop,
    getLivroTotaisPorAcumulador,
    // DIFAL
    getDifalNotas,
    getDifalResumo,
    getDifalPorUf,
    getDifalCarteiraKpis,
    getDifalCarteiraEmpresas,
  };
};
